import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { FresnelParameters } from '@babylonjs/core/Materials/fresnelParameters';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { RenderTargetTexture } from '@babylonjs/core/Materials/Textures/renderTargetTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { ReflectionProbe } from '@babylonjs/core/Probes/reflectionProbe';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import {
  BOARD_CENTER,
  COLORS,
  TABLE_HEIGHT,
  TABLE_RADIUS,
  TABLE_TOP_THICKNESS,
  TABLE_YAW,
} from './constants';
import { createTemple } from './temple';
import { createLandscape } from './landscape';
import { sunAt } from './sunCycle';

export interface Environment {
  sky: Mesh;
  /** Swap `emissiveTexture` for another gradient to change the time of day. */
  skyMaterial: StandardMaterial;
  /** The board's night sky. */
  nightSky: Texture;
  floor: Mesh;
  /** Six-sided dark walnut table the board rests on. */
  table: Mesh;
  /** Static reflection of the sky, cheap enough for the headset. */
  probe: ReflectionProbe;
  temple: Mesh;
  /** The temple's stone, gold and floor: what the temple's own sun lights (`createLighting`). */
  templeLit: Mesh[];
  /** The moving cloud veil and the mist banks on or off (off in the headset). */
  setClouds(on: boolean): void;
  update(seconds: number): void;
}

/**
 * The dome's built radius. It is centred on the camera, so this is a reach rather than a size.
 */
const SKY_RADIUS = 60;

export function createEnvironment(scene: Scene, parent: TransformNode | null = null): Environment {
  const sky = MeshBuilder.CreateSphere(
    'sky',
    { diameter: SKY_RADIUS * 2, segments: 12, sideOrientation: Mesh.BACKSIDE },
    scene,
  );
  const skyMaterial = new StandardMaterial('skyMaterial', scene);
  skyMaterial.disableLighting = true;
  skyMaterial.backFaceCulling = false;
  const nightSky = new Texture('/textures/temple/valley.png', scene, false, false);
  skyMaterial.emissiveTexture = nightSky;
  skyMaterial.diffuseColor = Color3.Black();
  skyMaterial.specularColor = Color3.Black();
  sky.material = skyMaterial;
  sky.infiniteDistance = true;
  sky.isPickable = false;
  /**
   * `infiniteDistance` keeps the dome centred on the camera, so its radius is not how big the sky
   * looks — from the inside a gradient sphere reads the same at any size — but how far the camera
   * can see past it. Whatever stands further off than the shell is behind it, and is painted over.
   *
   * Sixty units was ample for a board a handspan across and hid the arena completely: a world
   * thirty units wide, watched from ninety back, lies wholly outside a sixty-unit shell, so a duel
   * rendered as nothing but sky. Taking the radius from each camera's own far plane means the dome
   * holds whatever that camera can draw, on the table or over an arena, and neither has to know the
   * sky is there. It never shrinks below what it was built at, so the board is left as it was.
   */
  scene.onBeforeCameraRenderObservable.add((camera) => {
    sky.scaling.setAll(Math.max(1, (camera.maxZ * 0.9) / SKY_RADIUS));
  });

  const floor = MeshBuilder.CreateDisc('floor', { radius: 3.85, tessellation: 96 }, scene);
  floor.rotation.x = Math.PI / 2;
  floor.parent = parent;
  const floorMaterial = new StandardMaterial('floorMaterial', scene);
  floorMaterial.diffuseColor = Color3.FromHexString(COLORS.floor);
  floorMaterial.specularColor = new Color3(0.08, 0.08, 0.1);
  floor.material = floorMaterial;
  // The sun is low: piece shadows would land on the floor half a metre from the table.
  floor.receiveShadows = false;
  const temple = createTemple(scene, parent, floor);
  const landscape = createLandscape(scene, parent);
  const sunDisc = MeshBuilder.CreateSphere('visibleSun', { diameter: 3.0, segments: 12 }, scene);
  const glow = new StandardMaterial('sunGlow', scene);
  glow.disableLighting = true;
  glow.emissiveColor = new Color3(1, 0.88, 0.6);
  sunDisc.material = glow;
  sunDisc.isPickable = false;
  sunDisc.parent = parent;

  const probe = new ReflectionProbe('skyProbe', 128, scene);
  probe.renderList = [sky];
  probe.position.copyFrom(BOARD_CENTER);
  probe.refreshRate = RenderTargetTexture.REFRESHRATE_RENDER_ONCE;

  const table = createTable(scene, probe);
  table.parent = parent;

  return {
    sky,
    skyMaterial,
    nightSky,
    floor,
    table,
    probe,
    temple: temple.structure,
    templeLit: [temple.structure, temple.ornament, floor],
    setClouds: (on) => landscape.setClouds(on),
    update(seconds) {
      landscape.update(seconds);
      const sun = sunAt(seconds);
      sunDisc.position.set(sun.x * 120, sun.y * 120, BOARD_CENTER.z);
    },
  };
}

/**
 * Hexagonal walnut table: a thick six-sided top, a six-sided column and a base plate, merged into
 * one mesh with one material (two draw calls total with the reflection). Textures are CC0 from
 * ambientCG (Wood067), see CREDITS.md.
 */
function createTable(scene: Scene, probe: ReflectionProbe): Mesh {
  const top = MeshBuilder.CreateCylinder(
    'tableTop',
    { height: TABLE_TOP_THICKNESS, diameter: TABLE_RADIUS * 2, tessellation: 6 },
    scene,
  );
  top.position.y = TABLE_HEIGHT - TABLE_TOP_THICKNESS / 2;

  const columnHeight = TABLE_HEIGHT - TABLE_TOP_THICKNESS - 0.04;
  const column = MeshBuilder.CreateCylinder(
    'tableColumn',
    { height: columnHeight, diameterTop: 0.36, diameterBottom: 0.3, tessellation: 6 },
    scene,
  );
  column.position.y = 0.04 + columnHeight / 2;

  const base = MeshBuilder.CreateCylinder(
    'tableBase',
    { height: 0.04, diameterTop: 0.7, diameterBottom: 0.8, tessellation: 6 },
    scene,
  );
  base.position.y = 0.02;

  const table = Mesh.MergeMeshes([top, column, base], true, true, undefined, false, false)!;
  table.name = 'table';
  table.position.x = BOARD_CENTER.x;
  table.position.z = BOARD_CENTER.z;
  // A flat side towards the player, with the tablets along it (see TABLE_YAW: a sixth of a turn
  // here pointed a corner at them, Babylon's prism starting with a corner on +X).
  table.rotation.y = TABLE_YAW;
  table.receiveShadows = true;
  table.isPickable = false;

  const material = new StandardMaterial('walnut', scene);
  const albedo = new Texture('/textures/wood/walnut_color.jpg', scene);
  const normal = new Texture('/textures/wood/walnut_normal.jpg', scene);
  for (const t of [albedo, normal]) {
    t.uScale = 1.6;
    t.vScale = 1.6;
  }
  material.diffuseTexture = albedo;
  material.bumpTexture = normal;
  material.specularColor = new Color3(0.22, 0.2, 0.18);
  material.specularPower = 24;
  material.reflectionTexture = probe.cubeTexture;
  material.reflectionFresnelParameters = new FresnelParameters({
    bias: 0.02,
    power: 3,
    leftColor: new Color3(0.35, 0.35, 0.35),
    rightColor: Color3.Black(),
  });
  table.material = material;
  return table;
}

/** Vertical gradient for the sky dome: colour stops from the zenith (0) to the nadir (1). */
export function createGradientTexture(
  scene: Scene,
  name: string,
  stops: [number, string][],
): DynamicTexture {
  const texture = new DynamicTexture(name, { width: 4, height: 256 }, scene, false);
  const ctx = texture.getContext() as CanvasRenderingContext2D;
  const gradient = ctx.createLinearGradient(0, 0, 0, 256);
  for (const [at, color] of stops) gradient.addColorStop(at, color);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 4, 256);
  texture.update(false);
  texture.wrapU = Texture.CLAMP_ADDRESSMODE;
  texture.wrapV = Texture.CLAMP_ADDRESSMODE;
  return texture;
}

export const TABLE_UP = new Vector3(0, 1, 0);
